import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  useTheme,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import AutoFixNormalIcon from '@mui/icons-material/AutoFixNormal';
import InfoIcon from '@mui/icons-material/Info';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import LoginIcon from '@mui/icons-material/Login';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { authService } from '../services/authService';
import { errorHandler } from '../utils/errorHandler';

const Navbar = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const unsubscribe = authService.onAuthStateChange((user) => {
      setIsLoggedIn(!!user);
    });
    return () => unsubscribe();
  }, []);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
  };

  const handleLogout = async () => {
    try {
      await authService.logout();
      handleClose();
      handleMobileMenuClose();
      navigate('/');
    } catch (error) {
      errorHandler.handleError(error, 'Error signing out');
    }
  };

  const handleProfile = () => {
    handleClose();
    handleMobileMenuClose();
    navigate('/profile');
  };

  const handleBooking = () => {
    handleClose();
    handleMobileMenuClose();
    navigate('/booking');
  };

  const handleNavigation = (path: string) => {
    handleMobileMenuClose();
    navigate(path);
  };

  const menuItems = [
    { text: 'Ana Sayfa', path: '/', icon: <HomeIcon /> },
    { text: 'Tarot Falı', path: '/tarot-reading', icon: <AutoFixNormalIcon /> },
    { text: 'Hakkımızda', path: '/about', icon: <InfoIcon /> },
    { text: 'İletişim', path: '/contact', icon: <ContactMailIcon /> },
  ];

  const renderMobileDrawer = () => (
    <Drawer
      anchor="right"
      open={mobileMenuOpen}
      onClose={handleMobileMenuClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: 240,
          boxSizing: 'border-box',
          mt: '56px',
        },
      }}
    >
      <List>
        {menuItems.map((item) => (
          <ListItem
            button
            key={item.path}
            onClick={() => handleNavigation(item.path)}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText primary={item.text} />
          </ListItem>
        ))}
        <Divider />
        {isLoggedIn ? (
          <>
            <ListItem button onClick={handleProfile}>
              <ListItemIcon><Avatar sx={{ width: 24, height: 24 }}><AutoFixHighIcon /></Avatar></ListItemIcon>
              <ListItemText primary="Profilim" />
            </ListItem>
            <ListItem button onClick={handleBooking}>
              <ListItemIcon><AutoFixNormalIcon /></ListItemIcon>
              <ListItemText primary="Randevu Al" />
            </ListItem>
            <ListItem button onClick={handleLogout}>
              <ListItemIcon><LoginIcon /></ListItemIcon>
              <ListItemText primary="Çıkış Yap" />
            </ListItem>
          </>
        ) : (
          <>
            <ListItem button onClick={() => handleNavigation('/login')}>
              <ListItemIcon><LoginIcon /></ListItemIcon>
              <ListItemText primary="Giriş Yap" />
            </ListItem>
            <ListItem button onClick={() => handleNavigation('/register')}>
              <ListItemIcon><PersonAddIcon /></ListItemIcon>
              <ListItemText primary="Kayıt Ol" />
            </ListItem>
          </>
        )}
      </List>
    </Drawer>
  );

  return (
    <AppBar position="static" color="transparent" elevation={0}>
      <Container maxWidth="lg">
        <Toolbar>
          <AutoFixHighIcon sx={{ mr: 2, color: 'primary.main' }} />
          <Typography
            variant="h6"
            component={RouterLink}
            to="/"
            sx={{
              flexGrow: 1,
              textDecoration: 'none',
              color: 'primary.main',
              fontWeight: 'bold',
              '&:hover': {
                color: 'secondary.main',
              },
            }}
          >
            ROSE TAROT
          </Typography>
          
          {isMobile ? (
            <IconButton
              edge="end"
              color="inherit"
              aria-label="menu"
              onClick={handleMobileMenuToggle}
            >
              <MenuIcon />
            </IconButton>
          ) : (
            <Box sx={{ display: 'flex', gap: 2 }}>
              {menuItems.map((item) => (
                <Button
                  key={item.path}
                  component={RouterLink}
                  to={item.path}
                  color="inherit"
                  sx={{ '&:hover': { color: 'primary.main' } }}
                >
                  {item.text}
                </Button>
              ))}
              
              {isLoggedIn ? (
                <>
                  <IconButton
                    onClick={handleMenu}
                    sx={{
                      width: 40,
                      height: 40,
                      bgcolor: 'primary.main',
                      '&:hover': { bgcolor: 'primary.dark' }
                    }}
                  >
                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'transparent' }}>
                      <AutoFixHighIcon sx={{ color: '#fff' }} />
                    </Avatar>
                  </IconButton>
                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                  >
                    <MenuItem onClick={handleProfile}>Profilim</MenuItem>
                    <MenuItem onClick={handleBooking}>Randevu Al</MenuItem>
                    <MenuItem onClick={handleLogout}>Çıkış Yap</MenuItem>
                  </Menu>
                </>
              ) : (
                <>
                  <Button
                    component={RouterLink}
                    to="/login"
                    color="inherit"
                    sx={{ '&:hover': { color: 'primary.main' } }}
                  >
                    Giriş Yap
                  </Button>
                  <Button
                    component={RouterLink}
                    to="/register"
                    variant="contained"
                    color="primary"
                    sx={{
                      borderRadius: '20px',
                      '&:hover': { transform: 'scale(1.05)' }
                    }}
                  >
                    Kayıt Ol
                  </Button>
                </>
              )}
            </Box>
          )}
        </Toolbar>
      </Container>
      {renderMobileDrawer()}
    </AppBar>
  );
};

export default Navbar; 